import React from 'react';
import { useAudienceQuery } from 'src/api/queries/deliveryAnalyticsQueries';
import { DeliveryAnalyticsFilters } from 'src/types/TDeliveryAnalytics';
import { formatBillingBytes, formatNumberIntl } from 'src/utils/common';
import { AsyncSection, DataAsOfBadge, Section } from '../components/common';
import styles from '../styles.module.scss';

// Bucket for requests with no Referer (native apps, Referrer-Policy: no-referrer,
// direct hits). Often a large share for media delivery.
const DIRECT_UNKNOWN = '(direct / unknown)';

/** "Top delivery domains": observed embedding domains (referer_host) where assets
 *  are actually served. Complementary to the declared Channels view. Backed by the
 *  existing audience?dimension=referrer endpoint — host-only, 30-day window. */
const TopDomainsTab: React.FC<{ filters: DeliveryAnalyticsFilters }> = ({ filters }) => {
  const { data, isLoading, error } = useAudienceQuery({
    ...filters,
    dimension: 'referrer',
    limit: 100,
  });
  const rows = data?.data ?? [];
  const max = rows.reduce((m, r) => Math.max(m, r.requests), 0);

  return (
    <Section
      title="Top delivery domains"
      description="The embedding domains where your assets are actually delivered, by request volume. Embedding domain only (no path or query); reflects the last 30 days."
      headerRight={<DataAsOfBadge value={data?.data_as_of} loading={isLoading} />}
    >
      <AsyncSection isLoading={isLoading} error={error} isEmpty={rows.length === 0}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Domain</th>
              <th className={styles.numCol}>Requests</th>
              <th className={styles.numCol}>Bytes</th>
              <th className={styles.barCol}>Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.dimension || '(empty)'}>
                <td>{r.dimension || DIRECT_UNKNOWN}</td>
                <td className={styles.numCol}>{formatNumberIntl(r.requests)}</td>
                <td className={styles.numCol}>{formatBillingBytes(r.bytes)}</td>
                <td className={styles.barCol}>
                  <span
                    className={styles.bar}
                    style={{ width: max > 0 ? `${(r.requests / max) * 100}%` : '0%' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AsyncSection>
    </Section>
  );
};

export default TopDomainsTab;
